/*
 * @Author: liuxw@codoon.com
 * @Date: 2020-07-03 13:22:08
 * @Last Modified by: liuxw@codoon.com
 * @Last Modified time: 2021-07-14 16:25:08
 * @Content:
 */

/**
 * 1. url中需有参数 activityId，用来指定活动id
 * 2. 若url中有参数 emitAddress=1，则会在保存成功时将 JSON.stringify 后的地址存至 localStorage 中
 * 3. 进入该页面前，可在localStorage中设置表单默认值；设置后，就不用再在初始化时发获取地址的请求
 */
import React, {
  useEffect,
  useState,
  useCallback,
  useLayoutEffect,
} from "react";
import styles from "./index.style";
import "./index.less";
import { PROVINCES, CITIES } from "./constants";
import indexService, { ICounty } from "../../service/Index";
import utils from "../../utils/index";
import {
  ADDRESS_URL_PARAM_KEY,
  LOCAL_STORAGE_ADDRESS_KEY,
  LOCAL_STORAGE_DEFAULT_ADDRESS_KEY,
} from "../../constants/address";
import { useShowToast } from "../../hooks/utilHooks";
import classNames from "classnames";
import { isIosBangsDevice } from "../../utils/system";
import { IAddressDatum } from "../../service/activity/interface";
import { Toast } from "codoon-come";

const { GlobalToast } = Toast;

const PREFIX = "ActivityAddress";

const ADDRESS_FORM_ID = "codoon_activity_address";
const ARROW_URL =
  "https://s1.codooncdn.com/operation-tools/internal-user/2020-07-03T15.38.26/28x28x449xrH5QI0E8twELyi3Hph.png";

const DEFAULT_PROVINCE_ID = (PROVINCES[0] && PROVINCES[0].id) || "0";
const DEFAULT_CITY_ID =
  (CITIES[DEFAULT_PROVINCE_ID] &&
    CITIES[DEFAULT_PROVINCE_ID][0] &&
    CITIES[DEFAULT_PROVINCE_ID][0].id) ||
  "0";

export const Address: React.FC<{}> = React.memo(() => {
  const [name, setName] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [provinceId, setProvinceId] = useState<string>(DEFAULT_PROVINCE_ID);
  const [cityId, setCityId] = useState<string>(DEFAULT_CITY_ID);
  const [counties, setCounties] = useState<ICounty[]>([]);
  const [countyId, setCountyId] = useState<string>(DEFAULT_CITY_ID);
  const [defaultCountyName, setDefaultCountyName] = useState<string>("");
  const [detailAddress, setDetailAddress] = useState<string>("");
  const [isShowDesc, setEndDesc] = useState<boolean>(false);
  const [type, setType] = useState<number>(0);

  const { toastVisible, toastTip, showToast } = useShowToast();

  useLayoutEffect(() => {
    document.title = "活动收货地址填写";
  }, []);

  useEffect(() => {
    // 初始化address
    const urlParamMap = utils.getUrlParamMap();
    const activityId = parseInt(urlParamMap[ADDRESS_URL_PARAM_KEY.ACTIVITY_ID]);
    /**
     * 先判断localStorage是否有地址默认值
     */
    const defaultAddressStr = window.localStorage.getItem(
      LOCAL_STORAGE_DEFAULT_ADDRESS_KEY
    );
    if (defaultAddressStr) {
      window.localStorage.setItem(LOCAL_STORAGE_DEFAULT_ADDRESS_KEY, "");
      const defaultAddress: IAddressDatum = JSON.parse(defaultAddressStr);
      initFormValue(defaultAddress);
    } else {
      indexService
        .getMyActivity({
          activity_id: activityId,
        })
        .then((response) => {
          if (response.status == "OK") {
            const address = response.data.address_info;
            if (address) {
              initFormValue(address);
            }
            if (response.data) {
              response.data.type == 2 && setEndDesc(true);
              setType(response.data.type);
            }
          } else {
            showToast(response.description);
          }
        });
    }
  }, []);

  /**
   * 初始化表单值
   */
  const initFormValue = useCallback((address: IAddressDatum) => {
    setName(address.user_name);
    setPhone(address.user_phone);
    setDetailAddress(address.address_detail);
    const provinceIndex = utils.findIndex(
      PROVINCES,
      (province) => province.name == address.province_name
    );
    const provinceId =
      (PROVINCES[provinceIndex] && PROVINCES[provinceIndex].id) ||
      DEFAULT_PROVINCE_ID;
    setProvinceId(provinceId);
    const cities = CITIES[provinceId] || [];
    const cityIndex = utils.findIndex(
      cities,
      (city) => city.name == address.city_name
    );
    const cityId =
      (cities[cityIndex] && cities[cityIndex].id) || DEFAULT_CITY_ID;
    setCityId(cityId);
    // countyId在获取couties后再设置
    setDefaultCountyName(address.county_name);
  }, []);

  /**
   * 初始化countyId
   */
  useEffect(() => {
    if (defaultCountyName) {
      const countyIndex = utils.findIndex(
        counties,
        (county) => county.name == defaultCountyName
      );
      if (counties[countyIndex]) {
        setCountyId(`${counties[countyIndex].id}`);
        // 初始化一次即可，之后就置为空
        setDefaultCountyName("");
      }
    }
  }, [defaultCountyName, counties]);

  useEffect(() => {
    indexService.queryCountyInfo(cityId).then((response) => {
      if (response.data && Array.isArray(response.data)) {
        setCounties(response.data);
        setCountyId((response.data[0] && `${response.data[0].id}`) || "0");
      }
    });
  }, [cityId]);

  const onSave = useCallback(() => {
    let errMsg = "";
    if (!name) {
      errMsg = "请填写姓名";
    }
    if (!phone) {
      errMsg = "请填写手机号";
    }
    if (!detailAddress) {
      errMsg = "请填写详细地址";
    }
    if (errMsg) {
      showToast(errMsg);
      return;
    }

    const provinceIndex = utils.findIndex(
      PROVINCES,
      (province) => province.id == provinceId
    );
    const provinceName = PROVINCES[provinceIndex].name;
    const cities = CITIES[provinceId] || [];
    const cityIndex = utils.findIndex(cities, (city) => city.id == cityId);
    const cityName = cities[cityIndex].name;
    const countyIndex = utils.findIndex(
      counties,
      (county) => `${county.id}` == countyId
    );
    const countyName = counties[countyIndex].name;

    const addressDatum: IAddressDatum = {
      address_detail: detailAddress,
      user_name: name,
      user_phone: phone,
      province_name: provinceName,
      city_name: cityName,
      county_name: countyName,
    };

    const urlParamMap = utils.getUrlParamMap();
    const activityId = parseInt(urlParamMap[ADDRESS_URL_PARAM_KEY.ACTIVITY_ID]);
    if (activityId) {
      const strAddress = JSON.stringify(addressDatum);
      indexService
        .updateAddress({
          activity_id: activityId,
          act_type: Number(type),
          address: strAddress
        })
        .then((response) => {
          if (response.status == "OK") {
            showToast(`地址保存成功！`);
            // 存入localstorage
            if (urlParamMap[ADDRESS_URL_PARAM_KEY.EMIT_ADDRESS]) {
              window.localStorage.setItem(
                LOCAL_STORAGE_ADDRESS_KEY,
                strAddress
              );
            }
          } else {
            showToast(`地址保存失败：${response.description}`);
          }
        });
    }
  }, [name, phone, detailAddress, counties, cityId, provinceId, countyId]);

  const onNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.target && setName(event.target.value);
    },
    []
  );

  const onPhoneChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.target && setPhone(event.target.value);
    },
    []
  );

  const onProvinceChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      if (event.target) {
        const nextProvinceId = event.target.value;
        setProvinceId(nextProvinceId);
        const nextCityId =
          (CITIES[nextProvinceId] &&
            CITIES[nextProvinceId][0] &&
            CITIES[nextProvinceId][0].id) ||
          "0";
        setCityId(nextCityId);
      }
    },
    []
  );

  const onCityChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      event.target && setCityId(event.target.value);
    },
    []
  );

  const onCountyChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      if (event.target) {
        setCountyId(event.target.value);
      }
    },
    []
  );

  const onDetailAddressChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      event.target && setDetailAddress(event.target.value);
    },
    []
  );

  return (
    <div>
      {toastVisible && <GlobalToast text={toastTip} />}
      <form id={ADDRESS_FORM_ID}>
        <div style={styles.formItemWrapper}>
          <div style={styles.formItemLabel}>姓名</div>
          <div style={styles.formItemContent}>
            <input
              onChange={onNameChange}
              value={name}
              className={`${PREFIX}-input`}
              type="text"
              placeholder="请填写收货人姓名"
              maxLength={15}
            />
          </div>
        </div>
        <div style={styles.formItemWrapper}>
          <div style={styles.formItemLabel}>手机号码</div>
          <div style={styles.formItemContent}>
            <input
              onChange={onPhoneChange}
              value={phone}
              className={`${PREFIX}-input`}
              type="text"
              placeholder="请输入收货人手机号"
              maxLength={11}
            />
          </div>
        </div>
        <div style={styles.formItemWrapper}>
          <div style={styles.formItemLabel}>选择省份</div>
          <div style={styles.formItemContent}>
            <select
              value={provinceId}
              onChange={onProvinceChange}
              className={`${PREFIX}-select`}
            >
              {PROVINCES.map((province) => (
                <option value={province.id} key={province.id}>
                  {province.name}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.arrowIconWrapper}>
            <img style={styles.arrowIconWrapper} src={ARROW_URL} />
          </div>
        </div>
        <div style={styles.formItemWrapper}>
          <div style={styles.formItemLabel}>选择城市</div>
          <div style={styles.formItemContent}>
            <select
              value={cityId}
              onChange={onCityChange}
              className={`${PREFIX}-select`}
            >
              {CITIES[provinceId] &&
                CITIES[provinceId].map((city) => (
                  <option value={city.id} key={city.id}>
                    {city.name}
                  </option>
                ))}
            </select>
          </div>
          <div style={styles.arrowIconWrapper}>
            <img style={styles.arrowIconWrapper} src={ARROW_URL} />
          </div>
        </div>
        <div style={styles.formItemWrapper}>
          <div style={styles.formItemLabel}>选择区域</div>
          <div style={styles.formItemContent}>
            <select
              value={countyId}
              onChange={onCountyChange}
              className={`${PREFIX}-select`}
            >
              {counties.map((county) => (
                <option value={county.id} key={county.id}>
                  {county.name}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.arrowIconWrapper}>
            <img style={styles.arrowIconWrapper} src={ARROW_URL} />
          </div>
        </div>
        <div
          style={{
            ...styles.formItemWrapper,
            height: "fit-content",
            padding: "10px 16px",
          }}
        >
          <div style={styles.formItemContent}>
            <textarea
              onChange={onDetailAddressChange}
              value={detailAddress}
              className={`${PREFIX}-textarea`}
              placeholder="详细地址：如道路、门牌号、楼栋号、单元室"
              maxLength={60}
            />
          </div>
        </div>
      </form>
      {/* 官方活动-商品促销类型才显示 */}
      { isShowDesc ? <div className="actEndDesc">我们将在活动结束后的5个工作日内为您发货，请注意查收！</div> : ''}
      <div
        className={classNames(`${PREFIX}-footerBtn`, {
          ["is-iphonex"]: isIosBangsDevice,
        })}
      >
        <div className={`${PREFIX}-footerBtnContent`} onClick={onSave}>
          保存
        </div>
      </div>
    </div>
  );
});
